import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Building2, ChevronDown, ChevronUp, Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { fileUrl } from "@/components/Certificate";
import { PublicHeader } from "@/components/Layout";
import { SkeletonCardGrid } from "@/components/PageSkeleton";

const CATEGORY_ORDER = ["University", "High School", "Professional School"];
const CATEGORY_LABEL = { University: "Universities", "High School": "High Schools", "Professional School": "Professional Schools" };

export default function Registry() {
  const navigate = useNavigate();
  const [institutions, setInstitutions] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [instSearch, setInstSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/institutions", { params: { limit: 1000 } })
      .then(({ data }) => setInstitutions(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const q = instSearch.trim().toLowerCase();
    const out = {};
    CATEGORY_ORDER.forEach((c) => { out[c] = []; });
    institutions
      .filter((i) => i.active !== false)
      .filter((i) => !q || (i.name || "").toLowerCase().includes(q))
      .forEach((inst) => {
        const cat = CATEGORY_ORDER.includes(inst.category) ? inst.category : "University";
        out[cat].push(inst);
      });
    Object.keys(out).forEach((c) => {
      out[c].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    });
    return out;
  }, [institutions, instSearch]);

  const totalMatches = useMemo(
    () => CATEGORY_ORDER.reduce((n, c) => n + (grouped[c]?.length || 0), 0),
    [grouped]
  );
  const totalActive = institutions.filter((i) => i.active !== false).length;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <PublicHeader />
      <main className="w-full max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 dark:text-blue-400 mb-3">Partner registry</div>
            <h1 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight text-slate-900 dark:text-white" data-testid="registry-title">
              Registered institutions
            </h1>
            <p className="mt-3 text-base text-slate-600 dark:text-slate-400" data-testid="registry-subtitle">
              {instSearch.trim()
                ? `${totalMatches} ${totalMatches === 1 ? "match" : "matches"} for "${instSearch.trim()}"`
                : `${totalActive} ${totalActive === 1 ? "institution" : "institutions"} on the network.`}
            </p>
          </div>
          <div className="w-full md:w-80 relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              type="text"
              value={instSearch}
              onChange={(e) => setInstSearch(e.target.value)}
              placeholder="Search institutions by name…"
              className="h-11 pl-9 pr-9 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"
              data-testid="inst-search-input"
              aria-label="Search institutions by name"
            />
            {instSearch && (
              <button
                type="button"
                onClick={() => setInstSearch("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-1"
                aria-label="Clear search"
                data-testid="inst-search-clear-btn"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <SkeletonCardGrid count={6} />
        ) : totalMatches === 0 ? (
          <Card className="p-12 text-center border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900" data-testid="no-institutions">
            <Building2 className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500 dark:text-slate-400">
              {instSearch.trim() ? `No institutions match "${instSearch.trim()}".` : "No institutions onboarded yet."}
            </p>
            {instSearch.trim() && (
              <Button variant="outline" size="sm" onClick={() => setInstSearch("")} className="mt-4" data-testid="inst-search-reset-btn">
                Clear search
              </Button>
            )}
          </Card>
        ) : (
          <div className="space-y-12" data-testid="institutions-grid">
            {CATEGORY_ORDER.map((cat) => {
              const list = grouped[cat];
              if (!list || list.length === 0) return null;
              const searching = !!instSearch.trim();
              const isExpanded = searching || !!expanded[cat];
              const shown = isExpanded ? list : list.slice(0, 6);
              const remaining = list.length - shown.length;
              return (
                <div key={cat} data-testid={`group-${cat.replace(/\s+/g, "-").toLowerCase()}`}>
                  <div className="flex items-baseline justify-between mb-5 flex-wrap gap-2">
                    <h3 className="font-display font-bold text-xl md:text-2xl text-slate-900 dark:text-white flex items-baseline gap-3">
                      <span>{CATEGORY_LABEL[cat]}</span>
                      <span className="text-sm font-medium text-slate-500 dark:text-slate-400">{list.length}</span>
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {shown.map((inst, i) => (
                      <motion.div
                        key={inst.id}
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: i * 0.04 }}
                        onClick={() => navigate(`/institutions/${inst.slug}`)}
                        className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-7 hover:-translate-y-1 hover:shadow-lg dark:hover:shadow-black/40 hover:border-slate-300 dark:hover:border-slate-700 transition-all cursor-pointer"
                        data-testid={`inst-card-${inst.id}`}
                      >
                        <div className="flex items-start justify-between mb-5">
                          {inst.logo_path ? (
                            <img src={fileUrl(inst.logo_path)} alt="" className="w-11 h-11 object-contain rounded-md border border-slate-200 dark:border-slate-700 bg-white p-1" />
                          ) : (
                            <div className="w-11 h-11 rounded-md bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center border border-blue-100 dark:border-blue-900">
                              <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" strokeWidth={2.5} />
                            </div>
                          )}
                          <div className="text-right">
                            <div className="font-display font-extrabold text-2xl text-slate-900 dark:text-white leading-none">{inst.graduate_count}</div>
                            <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 dark:text-slate-400 mt-1">Graduates</div>
                          </div>
                        </div>
                        <h4 className="font-display font-bold text-lg text-slate-900 dark:text-white leading-tight">{inst.name}</h4>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Est. {inst.year_established}</p>
                        <div className="mt-5 pt-5 border-t border-slate-100 dark:border-slate-800 space-y-1.5 text-sm text-slate-600 dark:text-slate-300">
                          <div className="truncate">{inst.address}</div>
                          <div className="truncate">{inst.email}</div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  {!searching && list.length > 6 && (
                    <div className="mt-6 flex justify-center">
                      <Button
                        variant="outline"
                        onClick={() => setExpanded((e) => ({ ...e, [cat]: !isExpanded }))}
                        className="rounded-full"
                        data-testid={`expand-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        {isExpanded ? (<><ChevronUp className="w-4 h-4 mr-2" /> Show less</>) : (<><ChevronDown className="w-4 h-4 mr-2" /> Show {remaining} more</>)}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
